import { gazeAxes, REST_GAZE, type HeadGaze } from './face'
import { TAU, clamp, lerp, r2 } from './math'
import { PROFILES, PROFILE_SAMPLES, type ProfileName } from './profiles'

export interface Point {
  x: number
  y: number
}

/**
 * A silhouette = a radial profile r(theta) plus a pose.
 *
 * Everything goes through profiles sampled at the SAME number of angles: any two
 * shapes therefore have point-wise correspondence, and morphing reduces to linear
 * radius interpolation. That's what makes transitions clean without a path-morphing
 * library.
 */
export interface Silhouette {
  radii: number[]
  /** profile rotation, in radians */
  rot: number
  /** centre offset, in ball-radius units */
  cx: number
  cy: number
  /** squash & stretch, applied in screen space (after rotation) */
  sx: number
  sy: number
}

const ANGLES = Array.from({ length: PROFILE_SAMPLES }, (_, i) => (i / PROFILE_SAMPLES) * TAU)
const COS = ANGLES.map(Math.cos)
const SIN = ANGLES.map(Math.sin)

export function silhouette(name: ProfileName, pose: Partial<Silhouette> = {}): Silhouette {
  return {
    radii: [...PROFILES[name]],
    rot: 0,
    cx: 0,
    cy: 0,
    sx: 1,
    sy: 1,
    ...pose
  }
}

/** Perfect circle: neutral base (dot, bubble, fade target). */
export function circle(radius: number, pose: Partial<Silhouette> = {}): Silhouette {
  return {
    radii: new Array(PROFILE_SAMPLES).fill(radius),
    rot: 0,
    cx: 0,
    cy: 0,
    sx: 1,
    sy: 1,
    ...pose
  }
}

/** Interpolation of two silhouettes. `out` is reused to avoid allocating at 60 fps. */
export function blend(a: Silhouette, b: Silhouette, t: number, out?: Silhouette): Silhouette {
  const dst = out ?? { radii: new Array<number>(PROFILE_SAMPLES), rot: 0, cx: 0, cy: 0, sx: 1, sy: 1 }
  for (let i = 0; i < PROFILE_SAMPLES; i++) {
    dst.radii[i] = lerp(a.radii[i] ?? 1, b.radii[i] ?? 1, t)
  }
  // Shortest-path rotation: avoids a full turn when going e.g. from +170deg to -170deg.
  let dRot = b.rot - a.rot
  while (dRot > Math.PI) dRot -= TAU
  while (dRot < -Math.PI) dRot += TAU
  dst.rot = a.rot + dRot * t
  dst.cx = lerp(a.cx, b.cx, t)
  dst.cy = lerp(a.cy, b.cy, t)
  dst.sx = lerp(a.sx, b.sx, t)
  dst.sy = lerp(a.sy, b.sy, t)
  return dst
}

/** Projects the silhouette to screen points. `scale` = ball radius in viewBox units. */
export function toPoints(s: Silhouette, scale: number, out: Point[] = []): Point[] {
  const cr = Math.cos(s.rot)
  const sr = Math.sin(s.rot)
  for (let i = 0; i < PROFILE_SAMPLES; i++) {
    const r = s.radii[i] ?? 1
    const x = r * (COS[i] ?? 0)
    const y = r * (SIN[i] ?? 0)
    // rotation then squash in screen space, then translation
    const rx = x * cr - y * sr
    const ry = x * sr + y * cr
    const p = out[i] ?? { x: 0, y: 0 }
    p.x = (rx * s.sx + s.cx) * scale
    p.y = (ry * s.sy + s.cy) * scale
    out[i] = p
  }
  out.length = PROFILE_SAMPLES
  return out
}

/** Face-on head — the measured 2D star is this view, not `REST_GAZE`. */
const FACE_ON: HeadGaze = { yaw: 0, pitch: 0, roll: 0 }

/**
 * cos(4θ) on the planted rays. The measured sparkle is a0 + a4 cos(4θ);
 * every other Fourier mode is numerically zero. Re-binning a 3D mesh by
 * atan2 walks this phase (~20°) and turns idle into a different glyph.
 */
const COS4 = COS.map((c, i) => 1 - 8 * c * c * (SIN[i] ?? 0) ** 2)

/** Measured sparkle waist — thickness term on mode 0 only. */
const SPARKLE_T = 0.39
const FORESHORT_MU = 0.22
const FORESHORT_LAMBDA = 0.28
/**
 * Look-side tips grow, far tips shrink. Applied per lobe from the tip's
 * depth only — a continuous depth * tip skews the peak off the planted axis.
 * Strong enough to read during loaders/agents (weak values vanished under motion).
 */
const LOOK_SIDE = 0.95
/** Tip sample indices on 64 rays (0°, 90°, 180°, 270°). */
const TIP_IDX = [0, 16, 32, 48] as const
/** Soft clamp before tip-mean normalize — stops a single lobe from vanishing. */
const LOOK_SCALE_MIN = 0.78
const LOOK_SCALE_MAX = 1.42

/** Torso fraction of face gaze. REST is the design pose; expressions keep the sparkle. */
export const BODY_GAZE_DAMP = 0.28

/** Body uses REST plus a damped face delta. Roll stays the face calligraphy, not a twist. */
export function bodyGaze(face: HeadGaze, damp = BODY_GAZE_DAMP): HeadGaze {
  return {
    yaw: REST_GAZE.yaw + damp * (face.yaw - REST_GAZE.yaw),
    pitch: REST_GAZE.pitch + damp * (face.pitch - REST_GAZE.pitch),
    roll: REST_GAZE.roll
  }
}

/** Roll is eye calligraphy — the body never twists. */
function bodyAxes(gaze: HeadGaze) {
  return gazeAxes({ yaw: gaze.yaw, pitch: gaze.pitch, roll: 0 })
}

/**
 * Softmax blend of tip scales onto planted rays. Hard bumps went to
 * weight 0 at every waist and pinched the clefts (Catmull-Rom then jagged).
 * Blending *scales* (not depths) keeps waists near the tip-mean — depth blend
 * still warped the clefts when neighboring tips disagreed.
 */
function tipLockedScale(i: number, tipScale: number[], tipIdx: readonly number[]): number {
  const n = PROFILE_SAMPLES
  let sum = 0
  let wsum = 0
  for (let t = 0; t < tipIdx.length; t++) {
    let d = i - tipIdx[t]!
    if (d > n / 2) d -= n
    if (d < -n / 2) d += n
    const w = Math.exp(-(d * d) / 20)
    sum += w * (tipScale[t] ?? 1)
    wsum += w
  }
  return wsum > 1e-9 ? sum / wsum : 1
}

/**
 * Facing-up ρ, then: mode-0/4 foreshorten + look-side tip scale.
 * Planted rays only — no atan2 rebin. Face-on is the input.
 */
function foreshortenStarRadii(radii: number[], gaze: HeadGaze): number[] {
  const { f, right, down } = bodyAxes(gaze)
  const q = 1 - f[2] * f[2]
  const S0 = 1 + FORESHORT_MU * SPARKLE_T * q
  const phi = 1 - FORESHORT_LAMBDA * q
  const n = PROFILE_SAMPLES
  let a4 = 0
  for (let i = 0; i < n; i++) a4 += (radii[i] ?? 0) * (COS4[i] ?? 1)
  a4 *= 2 / n
  const tipDepth = TIP_IDX.map((ti) => {
    const dx = COS[ti] ?? 1
    const dy = SIN[ti] ?? 0
    return dx * right[2] + dy * down[2]
  })
  // Perspective: tips toward the camera grow, far tips shrink. Looking right
  // lengthens the left lobe — not the look-side tip (that read as reverse
  // perspective). Eyes nest via pocket seating, not by inflating their tip.
  // Far shrink is gentle: hard crush stubbed the opposite corners (top/bottom
  // left while looking up-right) while the look-side tip stayed perfect.
  let tipScaleSum = 0
  const tipScale = tipDepth.map((d) => {
    const biased = d > 0 ? d : d * 0.18
    const s = clamp(1 + LOOK_SIDE * biased, LOOK_SCALE_MIN, LOOK_SCALE_MAX)
    tipScaleSum += s
    return s
  })
  const tipScaleMean = tipScaleSum / tipScale.length
  // Scale tip *excess* only. Whole-ray scale warps the waists and Catmull-Rom
  // then pinches the clefts. Fixed waist = smooth notches; look reads on tips.
  const mode4 = new Array<number>(n)
  let mode4Waist = Infinity
  for (let i = 0; i < n; i++) {
    const rho = radii[i] ?? 0
    mode4[i] = S0 * (rho + (phi - 1) * a4 * (COS4[i] ?? 1))
    mode4Waist = Math.min(mode4Waist, mode4[i]!)
  }
  const live = new Array<number>(n)
  for (let i = 0; i < n; i++) {
    const s = tipLockedScale(i, tipScale, TIP_IDX) / tipScaleMean
    live[i] = mode4Waist + (mode4[i]! - mode4Waist) * s
  }
  return live
}

export interface VolumeSheen {
  cx: number
  cy: number
  rx: number
  ry: number
  opacity: number
}

/** Soft look-side highlight. Hard-edged disc read as a face plate. */
export function starVolumeSheen(gaze: HeadGaze, scale: number, amount = 1): VolumeSheen | null {
  const k = clamp(amount, 0, 1)
  if (k <= 0) return null
  const { f } = bodyAxes(gaze)
  const tilt = Math.hypot(f[0], f[1])
  if (tilt < 0.06) return null
  // Visible at a glance during orbit/agent motion — was ~0.12 and vanished.
  const opacity = k * 0.22 * clamp(tilt / 0.4)
  if (opacity < 0.04) return null
  return {
    cx: r2(f[0] * scale * 0.34),
    cy: r2(f[1] * scale * 0.34),
    rx: r2(scale * 0.72),
    ry: r2(scale * 0.58),
    opacity: r2(opacity)
  }
}

/** World-z of a face-plane lobe — toward the camera is positive. */
function lobeDepth(gaze: HeadGaze, dx: number, dy: number): number {
  const { right, down } = bodyAxes(gaze)
  return dx * right[2] + dy * down[2]
}

/** Projected radius over the face-on radius. Face-on is 1. */
export function starVolumeScale(i: number, radii: number[], gaze: HeadGaze): number {
  const r = radii[i] ?? 1
  if (r <= 1e-9) return 1
  return (starVolumeRadii(radii, gaze, 1)[i] ?? r) / r
}

/** Volume-adjusted radii. Face-on yaw/pitch is the input profile. Roll never enters. */
export function starVolumeRadii(radii: number[], gaze: HeadGaze, amount = 1): number[] {
  const k = clamp(amount, 0, 1)
  if (k <= 0 || (gaze.yaw === FACE_ON.yaw && gaze.pitch === FACE_ON.pitch)) {
    return radii
  }
  const live = foreshortenStarRadii(radii, gaze)
  if (k >= 1) return live
  return radii.map((r, i) => lerp(r, live[i] ?? r, k))
}

/**
 * Round / scalloped disc volume (Jovi / Mickey shirt). Same sphere axes as the
 * star, but tip-locked on the *ground ears only* — the dome is a disc and must
 * stay a smooth arc. Seeding the crown (−90°) as a tip carved Catmull-Rom flats
 * into the top when look-side scale disagreed across neighbours.
 */
const ROUND_LOOK = 0.92
const ROUND_SQUASH = 0.1
const ROUND_SCALE_MIN = 0.8
const ROUND_SCALE_MAX = 1.36
/** Mickey ground ears only: +x / −x scallops (not the dome crown). */
const ROUND_TIP_IDX: readonly number[] = (() => {
  const n = PROFILE_SAMPLES
  const tips = [Math.atan2(0.5, 0.5), Math.atan2(0.5, -0.5)]
  return tips.map((a) => {
    let t = a / TAU
    t = ((t % 1) + 1) % 1
    return Math.round(t * n) % n
  })
})()

/** Body turn from face on round shapes — stronger than the sparkle's plant. */
export const ROUND_BODY_DAMP = 0.72

function foreshortenRoundRadii(radii: number[], gaze: HeadGaze): number[] {
  const { f, right, down } = bodyAxes(gaze)
  const q = 1 - f[2] * f[2]
  const squash = 1 - ROUND_SQUASH * q
  const tipDepth = ROUND_TIP_IDX.map((ti) => {
    const dx = COS[ti] ?? 1
    const dy = SIN[ti] ?? 0
    return dx * right[2] + dy * down[2]
  })
  let tipScaleSum = 0
  const tipScale = tipDepth.map((d) => {
    // Far ear must actually recede — 0.14 left it glued on the silhouette.
    const biased = d > 0 ? d : d * 0.22
    const s = clamp(1 + ROUND_LOOK * biased, ROUND_SCALE_MIN, ROUND_SCALE_MAX)
    tipScaleSum += s
    return s
  })
  const tipScaleMean = tipScaleSum / tipScale.length
  // Disc floor — scale ear *excess* only so the crown stays a true arc.
  const floor = Math.min(...radii)
  const n = PROFILE_SAMPLES
  const live = new Array<number>(n)
  for (let i = 0; i < n; i++) {
    const s = tipLockedScale(i, tipScale, ROUND_TIP_IDX) / tipScaleMean
    const rho = radii[i] ?? 0
    live[i] = squash * (floor + (rho - floor) * s)
  }
  return live
}

/** Round-body volume radii (planet). Face-on is identity. */
export function roundVolumeRadii(radii: number[], gaze: HeadGaze, amount = 1): number[] {
  const k = clamp(amount, 0, 1)
  if (k <= 0 || (gaze.yaw === FACE_ON.yaw && gaze.pitch === FACE_ON.pitch)) {
    return radii
  }
  const live = foreshortenRoundRadii(radii, gaze)
  if (k >= 1) return live
  return radii.map((r, i) => lerp(r, live[i] ?? r, k))
}

/**
 * Peta — five soft lobes. Tip seeds at the lobe axes (one up), softmax-blended
 * like the star but without cos(4θ). Strong enough to turn under free-look, but
 * not so hot that a REST plant melts the face petal (eyes then sat in a side lobe).
 */
const PETA_LOOK = 0.78
const PETA_SQUASH = 0.1
const PETA_SCALE_MIN = 0.82
const PETA_SCALE_MAX = 1.32
const PETA_TIP_IDX: readonly number[] = (() => {
  const out: number[] = []
  for (let k = 0; k < 5; k++) {
    const a = -Math.PI / 2 + (k * Math.PI * 2) / 5
    let t = a / TAU
    t = ((t % 1) + 1) % 1
    out.push(Math.round(t * PROFILE_SAMPLES) % PROFILE_SAMPLES)
  }
  return out
})()

/** Body turn from face on Peta — same plant as Jovi, stronger than the sparkle. */
export const PETA_BODY_DAMP = 0.72

function foreshortenPetaRadii(radii: number[], gaze: HeadGaze): number[] {
  const { f, right, down } = bodyAxes(gaze)
  const q = 1 - f[2] * f[2]
  const squash = 1 - PETA_SQUASH * q
  const tipDepth = PETA_TIP_IDX.map((ti) => {
    const dx = COS[ti] ?? 1
    const dy = SIN[ti] ?? 0
    return dx * right[2] + dy * down[2]
  })
  let tipScaleSum = 0
  const tipScale = tipDepth.map((d) => {
    const biased = d > 0 ? d : d * 0.2
    const s = clamp(1 + PETA_LOOK * biased, PETA_SCALE_MIN, PETA_SCALE_MAX)
    tipScaleSum += s
    return s
  })
  const tipScaleMean = tipScaleSum / tipScale.length
  // Floor below the core so lobe excess carries the turn — not so low that
  // REST foreshortening (when amount > 0) eats the face petal.
  const floor = Math.min(...radii) * 0.92
  const n = PROFILE_SAMPLES
  const live = new Array<number>(n)
  for (let i = 0; i < n; i++) {
    const s = tipLockedScale(i, tipScale, PETA_TIP_IDX) / tipScaleMean
    const rho = radii[i] ?? 0
    live[i] = (floor + (rho - floor) * s) * squash
  }
  return live
}

/** Peta five-lobe volume radii. Face-on is identity. */
export function petaVolumeRadii(radii: number[], gaze: HeadGaze, amount = 1): number[] {
  const k = clamp(amount, 0, 1)
  if (k <= 0 || (gaze.yaw === FACE_ON.yaw && gaze.pitch === FACE_ON.pitch)) {
    return radii
  }
  const live = foreshortenPetaRadii(radii, gaze)
  if (k >= 1) return live
  return radii.map((r, i) => lerp(r, live[i] ?? r, k))
}

export function starLobeDepth(i: number, gaze: HeadGaze): number {
  return lobeDepth(gaze, COS[i] ?? 1, SIN[i] ?? 0)
}

function projectRadius(s: Silhouette, i: number, r: number, scale: number): Point {
  const cr = Math.cos(s.rot)
  const sr = Math.sin(s.rot)
  const x = r * (COS[i] ?? 0)
  const y = r * (SIN[i] ?? 0)
  const rx = x * cr - y * sr
  const ry = x * sr + y * cr
  return {
    x: (rx * s.sx + s.cx) * scale,
    y: (ry * s.sy + s.cy) * scale
  }
}

/**
 * Outer caps of the far lobes. The orbit uses this as a window — the body
 * stays one path. Complementary radius profiles left Catmull-Rom clefts.
 */
export function starFarTipCapPath(s: Silhouette, gaze: HeadGaze, scale: number): string | null {
  const radii = s.radii
  const n = radii.length
  let farPeak = 0
  for (let i = 0; i < n; i++) {
    if (starLobeDepth(i, gaze) < 0) farPeak = Math.max(farPeak, radii[i] ?? 0)
  }
  const cutoff = farPeak * 0.82
  const far = (i: number) => starLobeDepth(i, gaze) < 0 && (radii[i] ?? 0) > cutoff

  const seen = new Array<boolean>(n).fill(false)
  const runs: number[][] = []
  for (let i = 0; i < n; i++) {
    if (!far(i) || seen[i]) continue
    let j = i
    for (let step = 0; step < n && far((j - 1 + n) % n); step++) j = (j - 1 + n) % n
    const start = j
    const run: number[] = []
    do {
      run.push(j)
      seen[j] = true
      j = (j + 1) % n
    } while (j !== start && far(j))
    if (run.length >= 1) runs.push(run)
  }

  const parts: string[] = []
  for (const run of runs) {
    const before = (run[0]! - 1 + n) % n
    const after = (run[run.length - 1]! + 1) % n
    const pts = [
      projectRadius(s, before, cutoff, scale),
      ...run.map((i) => projectRadius(s, i, radii[i] ?? cutoff, scale)),
      projectRadius(s, after, cutoff, scale)
    ]
    const d = closedPath(pts)
    if (d) parts.push(d)
  }
  return parts.length ? parts.join('') : null
}

/**
 * The measured 2D star is the face-on equator. Volume is a foreshorten of
 * that profile on the same rays — not a rotated cookie, not a re-binned mesh.
 */
export function toPointsOnGaze(
  s: Silhouette,
  scale: number,
  gaze: HeadGaze,
  out: Point[] = [],
  amount = 1
): Point[] {
  return toPoints({ ...s, radii: starVolumeRadii(s.radii, gaze, amount) }, scale, out)
}

/**
 * Closed polyline -> Catmull-Rom cubics.
 *
 * With 64 points centred tangents are plenty: the contour is smooth to the pixel
 * even at 600 px, and the string stays short.
 */
export function closedPath(pts: Point[], tension = 1 / 6): string {
  const n = pts.length
  if (n < 3) return ''
  const first = pts[0]!
  let d = `M${r2(first.x)} ${r2(first.y)}`
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n]!
    const p1 = pts[i]!
    const p2 = pts[(i + 1) % n]!
    const p3 = pts[(i + 2) % n]!
    const c1x = p1.x + (p2.x - p0.x) * tension
    const c1y = p1.y + (p2.y - p0.y) * tension
    const c2x = p2.x - (p3.x - p1.x) * tension
    const c2y = p2.y - (p3.y - p1.y) * tension
    d += `C${r2(c1x)} ${r2(c1y)} ${r2(c2x)} ${r2(c2y)} ${r2(p2.x)} ${r2(p2.y)}`
  }
  return `${d}Z`
}

/**
 * Arbitrary polygon -> radial profile, by ray casting from `center`.
 *
 * Used to build shapes that don't express naturally as r(theta) (the trapezoidal
 * "!" bar). Computed once at load, never in the render loop.
 */
export function profileFromPolygon(poly: Point[], cx: number, cy: number): number[] {
  const radii = new Array<number>(PROFILE_SAMPLES).fill(0)
  const n = poly.length
  for (let k = 0; k < PROFILE_SAMPLES; k++) {
    const dx = COS[k] ?? 0
    const dy = SIN[k] ?? 0
    let best = 0
    for (let i = 0; i < n; i++) {
      const a = poly[i]!
      const b = poly[(i + 1) % n]!
      const ex = b.x - a.x
      const ey = b.y - a.y
      const den = dx * ey - dy * ex
      if (Math.abs(den) < 1e-9) continue
      const px = a.x - cx
      const py = a.y - cy
      const t = (px * ey - py * ex) / den // distance along the ray
      const u = (px * dy - py * dx) / den // position on the segment
      if (t > best && u >= 0 && u <= 1) best = t
    }
    radii[k] = best
  }
  return radii
}

/** Convex hull of two circles: the trapezoidal vertical "!" bar. */
export function hullOfCircles(
  x1: number,
  y1: number,
  r1: number,
  x2: number,
  y2: number,
  r2v: number,
  steps = 96
): Point[] {
  const dx = x2 - x1
  const dy = y2 - y1
  const dist = Math.hypot(dx, dy) || 1e-6
  // angle of common external tangents
  const base = Math.atan2(dy, dx)
  const spread = Math.acos(Math.max(-1, Math.min(1, (r1 - r2v) / dist)))
  const pts: Point[] = []
  // large circle arc
  for (let i = 0; i <= steps / 2; i++) {
    const a = base + spread + ((TAU - 2 * spread) * i) / (steps / 2)
    pts.push({ x: x1 + Math.cos(a) * r1, y: y1 + Math.sin(a) * r1 })
  }
  // small circle arc
  for (let i = 0; i <= steps / 2; i++) {
    const a = base - spread + ((2 * spread) * i) / (steps / 2)
    pts.push({ x: x2 + Math.cos(a) * r2v, y: y2 + Math.sin(a) * r2v })
  }
  return pts
}

/**
 * Profile radius in an arbitrary direction, by interpolating between neighbouring
 * samples.
 *
 * Used to snap anything placed "on" the body (eyes, notification badge) when the
 * silhouette is no longer a circle: without it, an eye at 0.62 radius sticks out
 * of a shape whose edge is at 0.55 in that direction, and the mask clips it.
 */
export function radiusAtAngle(radii: number[], angle: number): number {
  const n = radii.length
  const t = ((((angle / TAU) % 1) + 1) % 1) * n
  const i = Math.floor(t)
  return lerp(radii[i % n] ?? 1, radii[(i + 1) % n] ?? 1, t - i)
}

/**
 * Place a point that lives "on" the circle onto this silhouette's outline,
 * keeping its angle. That is the notification: −42° (upper-right), biting in
 * with the notch — a diamond's NE edge, a star's waist, the circle's rim.
 */
export function snapToOutline(
  x: number,
  y: number,
  radii: number[],
  rot: number
): { x: number; y: number } {
  const dist = Math.hypot(x, y) || 1
  const ang = Math.atan2(y, x)
  const r = dist * radiusAtAngle(radii, ang - rot)
  return { x: Math.cos(ang) * r, y: Math.sin(ang) * r }
}

/**
 * Superellipse: |x/sx|^n + |y/sy|^n = 1.
 * n = 2 gives an ellipse, n ~ 4 the customiser squircle.
 */
export function superellipseProfile(n: number, sx = 1, sy = 1): number[] {
  return ANGLES.map((_, i) => {
    const c = Math.abs((COS[i] ?? 0) / sx) ** n
    const s = Math.abs((SIN[i] ?? 0) / sy) ** n
    return (c + s) ** (-1 / n)
  })
}

/**
 * Radial profile of the UNION of discs: r(theta) = farthest ray/circle intersection.
 * Exact as long as the origin lies in the union — that's what gives the cloud lobes
 * without path booleans.
 */
export function unionOfCirclesProfile(circles: Array<{ x: number; y: number; r: number }>): number[] {
  const out = new Array<number>(PROFILE_SAMPLES).fill(0)
  for (let i = 0; i < PROFILE_SAMPLES; i++) {
    const dx = COS[i] ?? 0
    const dy = SIN[i] ?? 0
    let best = 0
    for (const c of circles) {
      const b = dx * c.x + dy * c.y
      const disc = b * b - (c.x * c.x + c.y * c.y - c.r * c.r)
      if (disc < 0) continue
      const t = b + Math.sqrt(disc)
      if (t > best) best = t
    }
    out[i] = best
  }
  return out
}

/**
 * Rounded-corner polygon, by Minkowski sum with a disc: each edge is pushed outward
 * by `rc`, each vertex becomes an arc of radius `rc`. Vertices should therefore be
 * placed at the desired radius MINUS rc.
 * Expects a clockwise polygon (screen frame, y down).
 */
export function roundedPolygon(verts: Point[], rc: number, arcSteps = 10): Point[] {
  const n = verts.length
  const out: Point[] = []
  const normal = (a: Point, b: Point) => {
    const dx = b.x - a.x
    const dy = b.y - a.y
    const len = Math.hypot(dx, dy) || 1
    // clockwise + y down: outward normal is (dy, -dx)
    return Math.atan2(-dx / len, dy / len)
  }
  for (let i = 0; i < n; i++) {
    const prev = verts[(i - 1 + n) % n]!
    const cur = verts[i]!
    const next = verts[(i + 1) % n]!
    const a0 = normal(prev, cur)
    const a1 = normal(cur, next)
    let d = a1 - a0
    while (d > Math.PI) d -= TAU
    while (d < -Math.PI) d += TAU
    for (let k = 0; k <= arcSteps; k++) {
      const a = a0 + (d * k) / arcSteps
      out.push({ x: cur.x + Math.cos(a) * rc, y: cur.y + Math.sin(a) * rc })
    }
  }
  return out
}

/** Regular polygon with rounded corners, inscribed in `radius`. */
export function regularPolygonProfile(
  sides: number,
  radius: number,
  rc: number,
  rotationDeg = 0
): number[] {
  const rot = (rotationDeg * Math.PI) / 180
  const verts = Array.from({ length: sides }, (_, i) => {
    // clockwise on screen: theta increases with y down
    const a = rot + (i / sides) * TAU
    return { x: Math.cos(a) * (radius - rc), y: Math.sin(a) * (radius - rc) }
  })
  return profileFromPolygon(roundedPolygon(verts, rc), 0, 0)
}

/** Exact closed polyline: keeps straight segments (unlike closedPath). */
export function polyPath(pts: Point[], scale = 1): string {
  if (pts.length < 3) return ''
  let d = ''
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]!
    d += `${i === 0 ? 'M' : 'L'}${r2(p.x * scale)} ${r2(p.y * scale)}`
  }
  return `${d}Z`
}

/** Capsule (stadium) centred on the origin: the bot's exact eye shape. */
export function capsulePath(w: number, h: number): string {
  const hw = Math.max(w, 0.01) / 2
  const hh = Math.max(h, 0.01) / 2
  const r = Math.min(hw, hh)
  return (
    `M${r2(-hw)} ${r2(-hh + r)}` +
    `A${r2(r)} ${r2(r)} 0 0 1 ${r2(-hw + r)} ${r2(-hh)}` +
    `L${r2(hw - r)} ${r2(-hh)}` +
    `A${r2(r)} ${r2(r)} 0 0 1 ${r2(hw)} ${r2(-hh + r)}` +
    `L${r2(hw)} ${r2(hh - r)}` +
    `A${r2(r)} ${r2(r)} 0 0 1 ${r2(hw - r)} ${r2(hh)}` +
    `L${r2(-hw + r)} ${r2(hh)}` +
    `A${r2(r)} ${r2(r)} 0 0 1 ${r2(-hw)} ${r2(hh - r)}Z`
  )
}

/**
 * Smile-eye: full-width dome top, straight sides, deep concave bottom (cheek push).
 *
 * Openness is baked into the path — NOT matrix vertical squash, which would flatten
 * the asymmetric curve back into a stadium oval. Height and pinch both collapse on blink.
 */
export function smileEyePath(w: number, h: number, open = 1): string {
  const hw = Math.max(w, 0.01) / 2
  const hh = Math.max(h, 0.01) / 2
  const o = clamp(open, 0.06, 1)

  if (o < 0.14) {
    const lash = hh * 0.07
    return (
      `M${r2(-hw * 0.9)} ${r2(lash)}` +
      `Q${r2(0)} ${r2(-lash * 0.6)} ${r2(hw * 0.9)} ${r2(lash)}` +
      `Z`
    )
  }

  const hScale = 0.12 + 0.88 * o
  const hhEff = hh * hScale
  const yBot = hhEff
  const pinch = lerp(hhEff * 0.4, hhEff * 0.94, 1 - o)
  const yCheek = hhEff - pinch
  const shoulder = lerp(hw * 0.22, hw, o)
  const yShoulder = -hhEff + shoulder
  return (
    `M${r2(-hw)} ${r2(yBot)}` +
    `Q${r2(0)} ${r2(yCheek)} ${r2(hw)} ${r2(yBot)}` +
    `L${r2(hw)} ${r2(yShoulder)}` +
    `A${r2(shoulder)} ${r2(shoulder)} 0 0 0 ${r2(-hw)} ${r2(yShoulder)}` +
    `Z`
  )
}

export type EyeForm = 'capsule' | 'smile'

export function eyePath(w: number, h: number, form: EyeForm = 'capsule', open = 1): string {
  return form === 'smile' ? smileEyePath(w, h, open) : capsulePath(w, h)
}
